import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Landmark, Loader2, CheckCircle2, AlertCircle, ChevronRight, Repeat } from "lucide-react";
import { toast } from "sonner";

/**
 * R151 — Resident dues page. Pulls open charges from /api/me/dues/summary
 * and offers a single-click "Pay all $X" that opens a Stripe Checkout
 * (card + ACH). Individual charge rows can be paid one at a time too.
 * Autopay setup + arbitrary payment stays on /resident/pay.
 */
export default function ResidentDuesPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // 'all' or a charge_id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/dues/summary");
      setSummary(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const payAll = async () => {
    if (!summary?.open_charges?.length) return;
    setPaying("all");
    try {
      const { data } = await api.post("/me/dues/pay", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Checkout failed");
      setPaying(null);
    }
  };

  const payOne = async (chargeId) => {
    setPaying(chargeId);
    try {
      const { data } = await api.post("/me/dues/pay", {
        origin_url: window.location.origin,
        charge_ids: [chargeId],
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Checkout failed");
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-white/50">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading your balance…
      </div>
    );
  }

  const owed = summary?.total_owed ?? 0;
  const openCount = summary?.open_count ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader eyebrow="Payments" title="Dues"
                   subtitle="Everything you owe your community in one place. Pay by card (instant) or bank account (2-day, lower fee)." />

      {/* Hero — total owed + Pay All CTA */}
      <Card className={`p-6 border-white/10 ${owed > 0
        ? "bg-gradient-to-br from-orange-500/[0.10] to-transparent border-orange-500/30"
        : "bg-gradient-to-br from-emerald-500/[0.08] to-transparent border-emerald-500/30"}`}>
        <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/50 mb-1">
              {owed > 0 ? "Current balance owed" : "You're all caught up"}
            </div>
            <div className={`text-5xl font-semibold tabular-nums ${owed > 0 ? "text-white" : "text-emerald-300"}`}
                 data-testid="dues-total-owed">
              ${owed.toFixed(2)}
            </div>
            <div className="text-xs text-white/50 mt-2">
              {owed > 0
                ? `${openCount} open charge${openCount === 1 ? "" : "s"}${summary?.next_due_date ? ` · next due ${new Date(summary.next_due_date).toLocaleDateString()}` : ""}`
                : "Nothing outstanding. Autopay keeps it that way — you can set that up on the payments page."}
            </div>
          </div>
          {owed > 0 && (
            <Button onClick={payAll}
                    disabled={paying !== null}
                    className="h-12 px-6 text-base rounded-full bg-orange-500 hover:bg-orange-600 text-black"
                    data-testid="dues-pay-all-btn">
              {paying === "all"
                ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Opening checkout…</>
                : <><CreditCard className="h-5 w-5 mr-2" /> Pay ${owed.toFixed(2)} now</>}
            </Button>
          )}
        </div>
      </Card>

      {/* Payment method status */}
      {summary?.payment_method?.has ? (
        <Card className="p-3 border-white/10 flex items-center gap-3 text-sm"
              data-testid="dues-pm-card">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">
              {summary.payment_method.brand?.toUpperCase() || "Card"} ending in •••{summary.payment_method.last4}
            </div>
            <div className="text-[11px] text-white/50">Saved for one-tap payments + autopay.</div>
          </div>
          <Link to="/resident/pay">
            <Button variant="outline" size="sm" className="border-white/10 rounded-full">
              <Repeat className="h-3.5 w-3.5 mr-1" /> Manage autopay
            </Button>
          </Link>
        </Card>
      ) : owed > 0 && (
        <Card className="p-3 border-amber-500/30 bg-amber-500/[0.05] flex items-center gap-3 text-sm"
              data-testid="dues-no-pm-card">
          <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-amber-200">No saved payment method yet</div>
            <div className="text-[11px] text-amber-200/70">
              You'll add one during checkout — or set up autopay first to save time next month.
            </div>
          </div>
          <Link to="/resident/pay">
            <Button variant="outline" size="sm"
                    className="border-amber-500/40 text-amber-200 rounded-full">
              Set up autopay <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </Link>
        </Card>
      )}

      {/* Open charges */}
      {openCount > 0 && (
        <Card className="p-0 border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="overline">Open charges</div>
            <div className="text-[11px] text-white/40">
              <Landmark className="h-3 w-3 inline mr-1" />
              Card + ACH accepted
            </div>
          </div>
          <ul className="divide-y divide-white/5">
            {summary.open_charges.map((c) => (
              <li key={c.id} className="px-4 py-3 flex items-center gap-3"
                  data-testid={`dues-charge-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{c.memo}</span>
                    {c.past_due && (
                      <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-300"
                             data-testid={`dues-past-due-${c.id}`}>
                        {c.days_past_due}d past due
                      </Badge>
                    )}
                    {c.kind === "late_fee" && (
                      <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300">
                        late fee
                      </Badge>
                    )}
                    {c.amount_paid > 0 && (
                      <Badge variant="outline" className="text-[10px] border-sky-500/40 text-sky-300">
                        partial · ${c.amount_paid.toFixed(2)} paid
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    {c.period && <span>Period {c.period} · </span>}
                    Due {c.due_date ? new Date(c.due_date).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-semibold tabular-nums"
                       data-testid={`dues-charge-amount-${c.id}`}>
                    ${c.amount_owed.toFixed(2)}
                  </div>
                </div>
                <Button size="sm" variant="outline"
                        disabled={paying !== null}
                        onClick={() => payOne(c.id)}
                        className="border-white/10 rounded-full h-8"
                        data-testid={`dues-pay-one-${c.id}`}>
                  {paying === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Pay"}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="text-[11px] text-white/40 text-center pt-2">
        Secure checkout via Stripe · card + US bank account (ACH) supported ·
        <Link to="/resident/pay" className="text-orange-300 hover:text-orange-200 ml-1">
          Custom payment or autopay →
        </Link>
      </div>
    </div>
  );
}
